import { Injectable } from '@angular/core';
import { NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router } from '@angular/router';
import { filter, Subscription } from 'rxjs';
import { SessionTimelineEntry } from '../types/session-timeline-entry';
import { RedactionService } from '../../security/redaction.service';
import { RouteMaskService } from '../../security/route-mask.service';
import { RingBuffer } from '../storage/ring-buffer';

@Injectable({
  providedIn: 'root',
})
export class SessionTimelineCollector {
  private readonly entries = new RingBuffer<SessionTimelineEntry>(80);
  private subscription?: Subscription;
  private started = false;

  constructor(
    private readonly router: Router,
    private readonly redactionService: RedactionService,
    private readonly routeMaskService: RouteMaskService,
  ) {}

  start(): void {
    if (this.started) {
      return;
    }

    this.started = true;
    this.record('session-start', 'Feedback session started', this.getCurrentRoute()); // i18n-ignore: diagnostic data
    this.subscription = this.router.events
      .pipe(
        filter(
          (event): event is NavigationStart | NavigationEnd | NavigationCancel | NavigationError =>
            event instanceof NavigationStart ||
            event instanceof NavigationEnd ||
            event instanceof NavigationCancel ||
            event instanceof NavigationError,
        ),
      )
      .subscribe((event) => {
        if (event instanceof NavigationStart) {
          this.record(
            'navigation-start',
            `Navigation started to ${this.normalizeRoute(event.url)}`, // i18n-ignore: diagnostic data
            this.normalizeRoute(event.url),
          );
          return;
        }

        if (event instanceof NavigationEnd) {
          const route = this.normalizeRoute(event.urlAfterRedirects);
          this.record('route-change', `Route changed to ${route}`, route); // i18n-ignore: diagnostic data
          return;
        }

        if (event instanceof NavigationCancel) {
          this.record(
            'navigation-cancel',
            `Navigation cancelled to ${this.normalizeRoute(event.url)}`, // i18n-ignore: diagnostic data
            this.normalizeRoute(event.url),
            this.redactionService.redactText(event.reason),
          );
          return;
        }

        if (event instanceof NavigationError) {
          this.record(
            'navigation-error',
            `Navigation error on ${this.normalizeRoute(event.url)}`, // i18n-ignore: diagnostic data
            this.normalizeRoute(event.url),
            this.redactionService.redactText(event.error),
          );
        }
      });
  }

  stop(): void {
    if (!this.started) {
      return;
    }

    this.started = false;
    this.subscription?.unsubscribe();
    this.subscription = undefined;
  }

  clear(): void {
    this.entries.clear();
  }

  getEntries(): readonly SessionTimelineEntry[] {
    return this.entries.toArray();
  }

  recordRequestFailure(method: string, url: string, status: number, duration: number, error?: string): void {
    this.record(
      'request-failure',
      `${method.toUpperCase()} ${status || 0} ${this.normalizeRoute(url)}`,
      this.normalizeRoute(url),
      this.redactionService.redactText(error ?? `Request failed in ${duration} ms`), // i18n-ignore: diagnostic data
    );
  }

  recordError(message: string, route: string, details?: string): void {
    this.record('error', message, route, details);
  }

  recordDebugAction(message: string, route: string, details?: string): void {
    this.record('debug-action', message, route, details);
  }

  private record(kind: SessionTimelineEntry['kind'], message: string, route: string, details?: string): void {
    this.entries.push({
      kind,
      message: this.redactionService.redactText(message),
      route: this.redactionService.redactText(route),
      timestamp: new Date().toISOString(),
      details: details ? this.redactionService.redactText(details) : undefined,
    });
  }

  private normalizeRoute(url: string): string {
    return this.routeMaskService.normalizeRoute(url);
  }

  private getCurrentRoute(): string {
    return this.normalizeRoute(
      this.router.url || window.location.pathname + window.location.search + window.location.hash,
    );
  }
}
